import React, { memo, useRef, useState, useEffect, useCallback } from "react";
import { View, StyleSheet } from "react-native";
import Colors from "../../../Resources/Color";
import WorkoutCard from "./WorkoutCard";
import { BlockPlanificationConstants } from "../../../Resources/Constants";

// Default height for placeholder when we haven't measured yet
const DEFAULT_PLACEHOLDER_HEIGHT =
  BlockPlanificationConstants.WORKOUT_MIN_HEIGHT;

// Preload margin in pixels (applies to all 4 sides for both horizontal and vertical scroll)
const PRELOAD_MARGIN = 400;

const getLegacyHeightKey = (exercisesExpanded = true) =>
  exercisesExpanded ? "expanded" : "collapsed";

const getHeightStateKey = (
  exercisesExpanded = true,
  showOnlyProtocolSlots = false
) => {
  const modeKey = showOnlyProtocolSlots ? "onlySlots" : "all";
  const expandedKey = exercisesExpanded ? "expanded" : "collapsed";
  return `${modeKey}:${expandedKey}`;
};

/**
 * Height cache - stores measured heights by workout ID and exercise expansion state
 * Structure: Map<workoutId, { expanded: height, collapsed: height }>
 * This persists across re-renders and allows accurate placeholder sizing
 * for workouts that have been previously rendered in different states
 *
 * Backward compatibility: Old entries may be just a number (expanded-only),
 * which will be treated as { expanded: height } when accessed
 */
const heightCache = new Map();

/**
 * Check if we have a cached height for a workout in the current state
 * @param {string} workoutId - The workout ID
 * @param {boolean} exercisesExpanded - Whether exercises are expanded (true) or collapsed (false)
 * @returns {boolean} - True if height is cached for the current state
 */
const hasCachedHeight = (
  workoutId,
  exercisesExpanded = true,
  showOnlyProtocolSlots = false
) => {
  if (!workoutId) return false;
  const cached = heightCache.get(workoutId);
  if (!cached) return false;

  // Handle backward compatibility: old entries are just numbers (expanded-only)
  if (typeof cached === "number") {
    return exercisesExpanded && !showOnlyProtocolSlots; // Only cached if expanded in all mode
  }

  // New structure: check if height exists for current state
  const stateKey = getHeightStateKey(exercisesExpanded, showOnlyProtocolSlots);
  if (cached[stateKey] !== undefined) return true;

  const legacyKey = getLegacyHeightKey(exercisesExpanded);
  return cached[legacyKey] !== undefined;
};

/**
 * Get cached height for a workout in the current state, with fallback logic
 * @param {string} workoutId - The workout ID
 * @param {boolean} exercisesExpanded - Whether exercises are expanded (true) or collapsed (false)
 * @returns {number} - Cached height for current state, or fallback height, or default
 */
const getCachedHeight = (
  workoutId,
  exercisesExpanded = true,
  showOnlyProtocolSlots = false
) => {
  if (!workoutId) return DEFAULT_PLACEHOLDER_HEIGHT;

  const cached = heightCache.get(workoutId);
  if (!cached) return DEFAULT_PLACEHOLDER_HEIGHT;

  // Handle backward compatibility: old entries are just numbers (expanded-only)
  if (typeof cached === "number") {
    if (exercisesExpanded && !showOnlyProtocolSlots) {
      return cached; // Use cached expanded height
    }
    // If collapsed but only expanded cached, use expanded as fallback (better than default)
    return cached;
  }

  // New structure: get height for current state
  const stateKey = getHeightStateKey(exercisesExpanded, showOnlyProtocolSlots);
  const currentStateHeight = cached[stateKey];

  if (currentStateHeight !== undefined) {
    return currentStateHeight;
  }

  // Fallback: try legacy key
  const legacyKey = getLegacyHeightKey(exercisesExpanded);
  if (cached[legacyKey] !== undefined) {
    return cached[legacyKey];
  }

  // Fallback: use height from other state if available (better than default)
  const fallbackKey = getHeightStateKey(
    !exercisesExpanded,
    showOnlyProtocolSlots
  );
  if (cached[fallbackKey] !== undefined) {
    return cached[fallbackKey];
  }

  const legacyFallbackKey = getLegacyHeightKey(!exercisesExpanded);
  if (cached[legacyFallbackKey] !== undefined) {
    return cached[legacyFallbackKey];
  }

  return DEFAULT_PLACEHOLDER_HEIGHT;
};

/**
 * Store measured height for a workout in the current state
 * @param {string} workoutId - The workout ID
 * @param {number} height - The measured height
 * @param {boolean} exercisesExpanded - Whether exercises are expanded (true) or collapsed (false)
 */
const setCachedHeight = (
  workoutId,
  height,
  exercisesExpanded = true,
  showOnlyProtocolSlots = false
) => {
  if (!workoutId || typeof height !== "number" || height <= 0) return;

  const stateKey = getHeightStateKey(exercisesExpanded, showOnlyProtocolSlots);
  const legacyKey = getLegacyHeightKey(exercisesExpanded);
  const current = heightCache.get(workoutId);

  // Handle backward compatibility: migrate old number entries to new structure
  if (typeof current === "number") {
    heightCache.set(workoutId, {
      [legacyKey]: current,
      [stateKey]: height,
    });
    return;
  }

  // New structure: update height for current state
  const updated = current || {};
  const existingHeight = updated[stateKey];

  // Only update if significantly different (avoid micro-updates)
  if (existingHeight === undefined || Math.abs(existingHeight - height) > 2) {
    updated[stateKey] = height;
    heightCache.set(workoutId, updated);
  }
};

/**
 * Check if an element is within or near the viewport using getBoundingClientRect
 * This is a fallback/verification for IntersectionObserver
 */
const isElementNearViewport = (element, margin = PRELOAD_MARGIN) => {
  if (!element) return false;

  try {
    const rect = element.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const windowWidth =
      window.innerWidth || document.documentElement.clientWidth;

    // Check if element is within viewport + margin on any side
    const isInVerticalRange =
      rect.bottom >= -margin && rect.top <= windowHeight + margin;
    const isInHorizontalRange =
      rect.right >= -margin && rect.left <= windowWidth + margin;

    return isInVerticalRange && isInHorizontalRange;
  } catch (e) {
    // If we can't determine, assume visible to be safe
    return true;
  }
};

/**
 * Check if an element is above or within the viewport (not below it)
 * Used to determine if an uncached workout needs to render to prevent layout shifts
 *
 * Returns true if:
 * - Element is above the viewport (would affect layout of content below)
 * - Element is within the viewport (visible)
 * - Element is partially visible
 *
 * Returns false if:
 * - Element is entirely below the viewport (doesn't affect visible content)
 */
const isElementAboveOrInViewport = (element, margin = PRELOAD_MARGIN) => {
  if (!element) return true; // Default to render if we can't determine

  try {
    const rect = element.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const windowWidth =
      window.innerWidth || document.documentElement.clientWidth;

    // Check if element is horizontally in range (considering horizontal scroll)
    const isInHorizontalRange =
      rect.right >= -margin && rect.left <= windowWidth + margin;

    // If not horizontally visible, don't force render
    if (!isInHorizontalRange) {
      return false;
    }

    // Element is above or within viewport if its top is above the bottom of viewport
    // This means: element.top < windowHeight + margin
    // (element could be above viewport, in viewport, or partially in viewport)
    const isAboveOrInViewport = rect.top < windowHeight + margin;

    return isAboveOrInViewport;
  } catch (e) {
    // If we can't determine, assume we need to render to be safe
    return true;
  }
};

/**
 * LazyWorkoutCard - Lazy-loads WorkoutCard using IntersectionObserver
 *
 * Renders the full WorkoutCard when visible OR when height hasn't been cached
 * AND the element is above/in the viewport (to prevent layout shifts).
 * Uses a dynamically-sized placeholder based on cached measurements.
 *
 * Key features:
 * - Smart lazy loading: Only force-renders uncached workouts that are above viewport
 * - Fast initial load: Workouts below viewport use placeholders even if uncached
 * - Zero layout shifts: Workouts above visible content always render to get measured
 * - Height caching: Measures and caches actual WorkoutCard height after render
 * - Accurate placeholders: Uses cached height for previously-rendered workouts
 * - rootMargin: 400px preload buffer on ALL sides (vertical AND horizontal)
 * - threshold: 0 ensures any partial visibility triggers loading
 * - hasBeenVisible: Keeps rendered once visible (prevents re-mount on small scrolls)
 *
 * Render decision:
 * - Visible → render
 * - Was visible → render (keep rendered)
 * - No cached height + above/in viewport → render (prevents layout shift)
 * - No cached height + below viewport → placeholder (safe, doesn't affect visible content)
 * - Has cached height + not visible → placeholder with cached height
 */
const LazyWorkoutCard = memo(
  ({
    workout,
    block,
    coach,
    t,
    formatWorkoutDate,
    handleDuplicateWorkout,
    exitWorkoutEditMode,
    handleCopyExercise,
    handlePasteExercise,
    handleDeleteWorkout,
    persistBlockChange,
    showSetPriority,
    rirOrRpe,
    isWeekHidden = false,
    forceRender = false,
    exercisesExpanded = true,
    showOnlyProtocolSlots = false,
    showExerciseHistory = true,
  }) => {
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [hasBeenVisible, setHasBeenVisible] = useState(false);
    const [isAboveViewport, setIsAboveViewport] = useState(false);
    const observerRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const checkTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);

    const workoutId = workout?.id;

    // Check if we have a cached height for this workout in the current state
    const hasHeight = hasCachedHeight(
      workoutId,
      exercisesExpanded,
      showOnlyProtocolSlots
    );

    // Get placeholder height from cache or use default (for current state)
    const placeholderHeight = getCachedHeight(
      workoutId,
      exercisesExpanded,
      showOnlyProtocolSlots
    );

    /**
     * Measure the WorkoutCard content and cache its height for the current state
     */
    const measureAndCacheHeight = useCallback(() => {
      if (!contentRef.current || !workoutId) return;

      try {
        const rect = contentRef.current.getBoundingClientRect();
        if (rect.height > 0) {
          setCachedHeight(
            workoutId,
            rect.height,
            exercisesExpanded,
            showOnlyProtocolSlots
          );
        }
      } catch (e) {
        // Ignore measurement errors
      }
    }, [workoutId, exercisesExpanded, showOnlyProtocolSlots]);

    // Setup IntersectionObserver for visibility detection
    useEffect(() => {
      isMountedRef.current = true;
      const element = containerRef.current;

      // Fallback: always render if IntersectionObserver is unavailable
      if (!element || typeof IntersectionObserver === "undefined") {
        setIsVisible(true);
        setHasBeenVisible(true);
        setIsAboveViewport(true);
        return;
      }

      /**
       * Check if element is above/in viewport (for uncached height logic)
       */
      const checkPositionForUncached = () => {
        if (!isMountedRef.current) return;
        const el = containerRef.current;
        if (el) {
          const aboveOrIn = isElementAboveOrInViewport(el);
          setIsAboveViewport(aboveOrIn);
        }
      };

      /**
       * Manual visibility check using getBoundingClientRect
       * Used as fallback if IntersectionObserver doesn't fire
       */
      const checkVisibilityManually = () => {
        if (!isMountedRef.current) return;
        if (hasBeenVisible) return; // Already loaded, no need to check

        const el = containerRef.current;
        if (el && isElementNearViewport(el)) {
          setIsVisible(true);
          setHasBeenVisible(true);
        }
      };

      // Create IntersectionObserver with generous margins on ALL sides
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (!isMountedRef.current) return;

          if (entry.isIntersecting) {
            setIsVisible(true);
            setHasBeenVisible(true);
            setIsAboveViewport(true);
          } else {
            setIsVisible(false);
            // Update position check when not intersecting
            checkPositionForUncached();
          }
        },
        {
          // Preload margin on all 4 sides: top, right, bottom, left
          rootMargin: `${PRELOAD_MARGIN}px`,
          // Trigger on any intersection (even 1 pixel)
          threshold: 0,
        }
      );

      observerRef.current.observe(element);

      // Immediate position and visibility check
      requestAnimationFrame(() => {
        if (!isMountedRef.current) return;
        checkPositionForUncached();
        checkVisibilityManually();
      });

      // Additional delayed check as extra safety net
      checkTimeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        checkPositionForUncached();
        checkVisibilityManually();
      }, 100);

      // Periodic visibility check as ultimate fallback
      const intervalId = setInterval(() => {
        if (!isMountedRef.current) return;

        // Always update position for uncached items
        if (!hasHeight) {
          checkPositionForUncached();
        }

        if (!hasBeenVisible) {
          checkVisibilityManually();
        } else {
          clearInterval(intervalId);
        }
      }, 500);

      return () => {
        isMountedRef.current = false;
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
        if (checkTimeoutRef.current) {
          clearTimeout(checkTimeoutRef.current);
        }
        clearInterval(intervalId);
      };
    }, [hasBeenVisible, hasHeight, exercisesExpanded]);

    // Render actual card if:
    // 1. Currently visible in viewport, OR
    // 2. Was previously visible (keep rendered), OR
    // 3. Height not cached AND element is above/in viewport (would cause layout shift for content below)
    //    - Workouts BELOW the viewport with no cached height can use placeholder safely
    const needsHeightMeasurement = !hasHeight && isAboveViewport;
    const shouldRender =
      forceRender || isVisible || hasBeenVisible || needsHeightMeasurement;

    // Setup ResizeObserver to track height changes when content is rendered
    // This will automatically detect height changes when exercisesExpanded state changes
    const shouldMeasure = shouldRender;

    useEffect(() => {
      if (!shouldMeasure) return;

      // Wait for content to render
      const setupResizeObserver = () => {
        const contentElement = contentRef.current;
        if (!contentElement || typeof ResizeObserver === "undefined") return;

        // Initial measurement (caches height for current exercisesExpanded state)
        measureAndCacheHeight();

        // Watch for size changes (e.g., edit mode toggle, exercises added, collapse/expand)
        // When exercisesExpanded changes, ResizeObserver will detect the height change
        // and update the cache with the new height for the new state
        resizeObserverRef.current = new ResizeObserver(() => {
          measureAndCacheHeight();
        });

        resizeObserverRef.current.observe(contentElement);
      };

      // Use requestAnimationFrame to ensure content is rendered
      const rafId = requestAnimationFrame(setupResizeObserver);

      return () => {
        cancelAnimationFrame(rafId);
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      };
    }, [shouldMeasure, measureAndCacheHeight, exercisesExpanded]);

    return (
      <View ref={containerRef} style={styles.lazyWrapper}>
        {shouldRender ? (
          <View ref={contentRef}>
            <WorkoutCard
              workout={workout}
              block={block}
              coach={coach}
              t={t}
              formatWorkoutDate={formatWorkoutDate}
              handleDuplicateWorkout={handleDuplicateWorkout}
              exitWorkoutEditMode={exitWorkoutEditMode}
              handleCopyExercise={handleCopyExercise}
              handlePasteExercise={handlePasteExercise}
              handleDeleteWorkout={handleDeleteWorkout}
              persistBlockChange={persistBlockChange}
              showSetPriority={showSetPriority}
              rirOrRpe={rirOrRpe}
              isWeekHidden={isWeekHidden}
              exercisesExpanded={exercisesExpanded}
              showExerciseHistory={showExerciseHistory}
            />
          </View>
        ) : (
          <View style={[styles.placeholder, { height: placeholderHeight }]}>
            {/* Header skeleton */}
            <View style={styles.placeholderHeader} />
            {/* Body skeleton - white background, flexes to fill space */}
            <View style={styles.placeholderBody} />
            {/* Footer skeleton */}
            <View style={styles.placeholderFooter} />
          </View>
        )}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  lazyWrapper: {
    // No fixed minHeight - let content or placeholder determine height
  },
  placeholder: {
    // Height is set dynamically via inline style
    backgroundColor: Colors.whiteColor,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    overflow: "hidden",
    flexDirection: "column",
  },
  placeholderHeader: {
    height: BlockPlanificationConstants.WORKOUT_HEADER_HEIGHT,
    backgroundColor: Colors.lightGrayColor,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderColor,
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
  },
  placeholderBody: {
    flex: 1,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 12,
    paddingBottom: 12,
    minHeight: 120,
  },
  placeholderFooter: {
    height: 40, // Approximate footer height (padding: 8 + content)
    borderTopWidth: 1,
    borderTopColor: Colors.borderColor,
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
    backgroundColor: Colors.whiteColor,
  },
});

export default LazyWorkoutCard;
